import moment from 'moment';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { formatMoney, formatNumber } from './leaveFormatters';

// 'money' is the synthetic money-pot unit.
export const LEAVE_BALANCE_UNIT = { Hours: 'hours', Days: 'days', Money: 'money' };

export const isMoneyBalance = (balance) => balance?.unit === LEAVE_BALANCE_UNIT.Money;

export const formatBalanceUnitSuffix = (balance, t) => {
  if (!t) return '';
  if (balance?.unit === LEAVE_BALANCE_UNIT.Hours) return t('leaveManagement_unitSuffix_hours');
  if (balance?.unit === LEAVE_BALANCE_UNIT.Days) return t('leaveManagement_unitSuffix_days');
  return '';
};

export const formatBalanceAmount = (amount, unit, t, formattingMap) =>
  unit === LEAVE_BALANCE_UNIT.Money
    ? formatMoney(formattingMap, amount)
    : `${formatNumber(amount)}${formatBalanceUnitSuffix({ unit }, t)}`;

export const formatBalanceLine = (balance, t) =>
  t('leaveManagement_balanceLine', {
    amount: balance.balance,
    unit: formatBalanceUnitSuffix(balance, t),
    name: balance.leaveTypeName ?? `#${balance.accountContractV2LeaveId}`
  });

const forEachDayInRange = (start, end, callback) => {
  const from = moment(start).startOf('day');
  const to = moment(end).startOf('day');
  if (!from.isValid() || !to.isValid()) return;
  for (const day = from; !day.isAfter(to, 'day'); day.add(1, 'day')) {
    callback(day);
  }
};

const holidayDateKeysCache = new WeakMap();

const isHolidayInBranch = (holiday, branchId) =>
  !holiday?.branchIds?.length || (!!branchId && holiday.branchIds.includes(branchId));

const toHolidayDateKeys = (holidays, branchId) => {
  if (!Array.isArray(holidays)) return new Set();
  const cacheByBranch = holidayDateKeysCache.get(holidays) ?? new Map();
  holidayDateKeysCache.set(holidays, cacheByBranch);
  const branchKey = branchId ?? 0;
  if (cacheByBranch.has(branchKey)) return cacheByBranch.get(branchKey);
  const keys = new Set();
  holidays.forEach((holiday) => {
    if (!holiday?.start || !holiday?.end || !isHolidayInBranch(holiday, branchId)) return;
    const startMoment = moment.utc(holiday.start);
    const endMoment = moment.utc(holiday.end);
    const lastDay =
      endMoment.isAfter(startMoment) && endMoment.isSame(endMoment.clone().startOf('day'))
        ? endMoment.clone().subtract(1, 'day')
        : endMoment;
    forEachDayInRange(startMoment, lastDay, (day) => keys.add(day.format(API_DATE_FORMAT)));
  });
  cacheByBranch.set(branchKey, keys);
  return keys;
};

export const countEligibleLeaveDays = (dates, excludedDays, excludedHolidays, branchId) => {
  const { startDate, endDate } = dates ?? {};
  if (!startDate || !endDate) return { eligible: 0, excluded: 0 };
  const isoWeekdays = new Set(excludedDays?.isoWeekdays ?? []);
  const holidays = excludedDays?.holidays ? toHolidayDateKeys(excludedHolidays, branchId) : new Set();
  let eligible = 0;
  let excluded = 0;
  forEachDayInRange(startDate, endDate, (day) => {
    const isExcludedWeekday = isoWeekdays.has(day.isoWeekday());
    const isHoliday = holidays.size > 0 && holidays.has(day.format(API_DATE_FORMAT));
    if (isExcludedWeekday || isHoliday) {
      excluded += 1;
    } else {
      eligible += 1;
    }
  });
  return { eligible, excluded };
};
